import {
  Page,
  Text,
  View,
  Document,
  Image,
  PDFViewer,
  StyleSheet,
} from "@react-pdf/renderer";
import React from "react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Request } from "../../hooks/Request";
import config from "../../config.json";

const styles = StyleSheet.create({
  viewer: {
    width: 900,
    height: window.innerHeight - 200,
  },
  page: {
    padding: "32pt 40pt",
    width: "100%",
    fontSize: 10,
    flexDirection: "column",
  },
  header: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
  },
  logoContainer: {
    width: 150,
    alignItems: "flex-start",
  },
  logo: {
    height: 57,
    width: 150,
    objectFit: "contain",
  },
  titleContainer: {
    flex: 1,
    alignItems: "center",
    marginTop: 8,
  },
  mainTitle: {
    fontFamily: "Helvetica-Bold",
    marginBottom: 4,
    textTransform: "uppercase",
  },
  headerRight: {
    flex: 1,
    alignItems: "flex-end",
    marginTop: 8,
    fontSize: 10,
  },
  dateRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
    fontSize: 10,
  },
  infoSection: {
    marginBottom: 8,
    fontSize: 10,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  infoLabel: {
    fontFamily: "Helvetica-Bold",
    marginRight: 4,
  },
  glossSection: {
    border: "1pt solid #666",
    padding: 8,
    marginBottom: 12,
    minHeight: 40,
    fontFamily: "Helvetica-Bold",
  },
  tableContainer: {
    width: "100%",
    marginBottom: 8,
  },
  tableHeader: {
    width: "100%",
    flexDirection: "row",
    border: "1pt solid #666",
    borderTop: "none",
  },
  headerCell: {
    textAlign: "center",
    borderRight: "1pt solid #666",
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    paddingTop: 4,
    paddingBottom: 15,
  },
  codeHeader: {
    width: "14%",
  },
  accountHeader: {
    flex: 1,
  },
  amountHeader: {
    width: "12%",
  },
  currencyHeader: {
    width: "24%",
    textAlign: "center",
    borderRight: "1pt solid #666",
    paddingTop: 2,
    paddingBottom: 8,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    borderBottom: "1pt solid #666",
  },
  tableRow: {
    width: "100%",
    flexDirection: "row",
    borderLeft: "1pt solid #666",
    borderRight: "1pt solid #666",
  },
  cell: {
    borderRight: "1pt solid #666",
    padding: "2pt 4pt",
  },
  codeCell: {
    width: "14%",
    fontSize: 9,
    lineHeight: 1.5,
  },
  accountCell: {
    flex: 1,
    fontSize: 9,
    lineHeight: 1.5,
  },
  amountCell: {
    width: "12%",
    textAlign: "right",
    fontSize: 9,
    paddingRight: 6,
  },
  emptyRow: {
    width: "100%",
    flexDirection: "row",
    borderLeft: "1pt solid #666",
    borderRight: "1pt solid #666",
    borderBottom: "1pt solid #666",
    flex: 1,
    minHeight: 100,
  },
  footer: {
    width: "100%",
    flexDirection: "row",
    border: "1pt solid #666",
    backgroundColor: "#f5f5f5",
  },
  descriptionLabel: {
    width: "14%",
    fontFamily: "Helvetica-Bold",
    padding: 8,
  },
  totalLabel: {
    flex: 1,
    textAlign: "right",
    fontFamily: "Helvetica-Bold",
    paddingRight: 10,
    padding: 8,
    borderRight: "1pt solid #666",
  },
  totalAmount: {
    width: "12%",
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
    paddingRight: 6,
    padding: 8,
    borderRight: "1pt solid #666",
  },
  signatureSection: {
    width: "100%",
    flexDirection: "row",
    border: "1pt solid #666",
    marginTop: 16,
    // minHeight: 160,
  },
  signatureBox: {
    flex: 1,
    borderRight: "1pt solid #666",
    paddingVertical: 8,
    justifyContent: "flex-end",
  },
  signatureBoxLast: {
    flex: 1.3,
    paddingVertical: 8,
    justifyContent: "space-between",
  },
  signatureLabel: {
    borderTop: "1pt solid #666",
    paddingTop: 4,
    textAlign: "center",
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
  },
  signatureField: {
    marginBottom: 8,
    fontSize: 9,
    borderBottom: "1pt solid #666",
  },
  signatureFieldLabel: {
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  pageNumber: {
    position: "absolute",
    fontSize: 10,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "grey",
  },
});

export const PdfVoucher = ({ id, type }) => {
  const { data, loading, call } = Request({
    urlApi: `${config.requestURLConta}/api/Voucher/report/${id}/${type}`,
  });

  if (!loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div>Loading...</div>
      </div>
    );
  }

  const items = data?.items || [];

  const totalDebitBs = items.reduce(
    (sum, item) => sum + (item.debitBs || 0),
    0
  );
  const totalAssetBs = items.reduce(
    (sum, item) => sum + (item.assetBs || 0),
    0
  );
  const totalDebitSus = items.reduce(
    (sum, item) => sum + (item.debitSus || 0),
    0
  );
  const totalAssetSus = items.reduce(
    (sum, item) => sum + (item.assetSus || 0),
    0
  );

  const numberWithDecimals = (number) => {
    if (!number) return "0.00";
    return parseFloat(number).toFixed(2);
  };

  const getVoucherTypeText = (type) => {
    switch (type) {
      case 0:
        return "TRASPASO";
      case 1:
        return "EGRESO";
      case 2:
        return "INGRESO";
      default:
        return "COMPROBANTE";
    }
  };

  const formatDate = () => {
    return dayjs(data.voucherDate).locale("es").format("DD/MM/YYYY");
  };

  return (
    <div>
      <PDFViewer style={styles.viewer}>
        <Document>
          <Page size="A4" style={styles.page}>
            <Text
              style={styles.pageNumber}
              fixed
              render={({ pageNumber, totalPages }) =>
                `Hoja: ${pageNumber} / ${totalPages}`
              }
            />

            <View style={styles.header}>
              <View style={styles.logoContainer}>
                <Image
                  src="https://res.cloudinary.com/degnu2idl/image/upload/v1682460459/gamarra/LOS_ANDES_GAMARRA_qmlzlz.png"
                  style={styles.logo}
                />
              </View>

              <View style={styles.titleContainer}>
                <Text style={styles.mainTitle}>
                  COMPROBANTE DE {getVoucherTypeText(data?.type)}
                </Text>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Fecha:</Text>
                  <Text>{formatDate()}</Text>
                </View>
              </View>

              <View style={styles.headerRight}>
                <Text style={{ fontWeight: "bold" }}>
                  CE-{data?.num || "-"}
                </Text>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>T/C Dolar:</Text>
                  <Text>
                    {data?.exchangeRate
                      ? numberWithDecimals(data.exchangeRate)
                      : "-"}
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.infoSection}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Cheque No.</Text>
                <Text>{data?.checkNum || "-"}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Cheque girado a:</Text>
                <Text>{data?.checkTo || "-"}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Glosa:</Text>
                <Text>{data?.gloss || "-"}</Text>
              </View>
            </View>

            <View style={styles.glossSection}>
              <Text>{data?.gloss || "-"}</Text>
            </View>

            <View style={styles.tableContainer}>
              <View
                style={{
                  flexDirection: "row",
                  border: "1pt solid #666",
                  borderBottom: "none",
                }}
              >
                <Text style={[styles.headerCell, styles.codeHeader]}></Text>
                <Text style={[styles.headerCell, styles.accountHeader]}></Text>
                <Text style={styles.currencyHeader}>BOLIVIANOS</Text>
                <Text style={[styles.currencyHeader, { borderRight: "none" }]}>
                  DOLARES
                </Text>
              </View>

              <View style={styles.tableHeader}>
                <Text style={[styles.headerCell, styles.codeHeader]}>
                  CÓDIGO
                </Text>
                <Text style={[styles.headerCell, styles.accountHeader]}>
                  DETALLE
                </Text>
                <Text style={[styles.headerCell, styles.amountHeader]}>
                  DEBE
                </Text>
                <Text style={[styles.headerCell, styles.amountHeader]}>
                  HABER
                </Text>
                <Text style={[styles.headerCell, styles.amountHeader]}>
                  DEBE
                </Text>
                <Text
                  style={[
                    styles.headerCell,
                    styles.amountHeader,
                    { borderRight: "none" },
                  ]}
                >
                  HABER
                </Text>
              </View>

              {items.map((item, index) => {
                const codeLines = String(item?.code || "-")
                  .split("\n")
                  .slice(-3)
                  .filter((line) => line.trim());
                const descLines = String(item?.description || "-")
                  .split("\n")
                  .slice(-3)
                  .filter((line) => line.trim());
                const maxLines = Math.max(codeLines.length, descLines.length);

                const rows = [];
                for (let i = 0; i < maxLines; i++) {
                  rows.push({
                    code: codeLines[i] || "",
                    desc: descLines[i] || "",
                  });
                }

                const isLastItem = index === items.length - 1;

                return (
                  <React.Fragment key={item.id || index}>
                    <View style={styles.tableRow}>
                      <View style={[styles.cell, styles.codeCell]}>
                        <Text>{rows[0].code}</Text>
                      </View>
                      <View style={[styles.cell, styles.accountCell]}>
                        <Text>{rows[0].desc}</Text>
                      </View>
                      <Text style={[styles.cell, styles.amountCell]}>
                        {numberWithDecimals(item.debitBs)}
                      </Text>
                      <Text style={[styles.cell, styles.amountCell]}>
                        {numberWithDecimals(item.assetBs)}
                      </Text>
                      <Text style={[styles.cell, styles.amountCell]}>
                        {numberWithDecimals(item.debitSus)}
                      </Text>
                      <Text
                        style={[
                          styles.cell,
                          styles.amountCell,
                          { borderRight: "none" },
                        ]}
                      >
                        {numberWithDecimals(item.assetSus)}
                      </Text>
                    </View>

                    {rows.slice(1).map((row, idx) => (
                      <View key={idx} style={styles.tableRow}>
                        <View style={[styles.cell, styles.codeCell]}>
                          <Text style={{ fontSize: 8 }}>{row.code}</Text>
                        </View>
                        <View style={[styles.cell, styles.accountCell]}>
                          <Text style={{ fontSize: 8 }}>{row.desc}</Text>
                        </View>
                        <Text style={[styles.cell, styles.amountCell]}></Text>
                        <Text style={[styles.cell, styles.amountCell]}></Text>
                        <Text style={[styles.cell, styles.amountCell]}></Text>
                        <Text
                          style={[
                            styles.cell,
                            styles.amountCell,
                            { borderRight: "none" },
                          ]}
                        ></Text>
                      </View>
                    ))}

                    {item.gloss && (
                      <View
                        style={[
                          styles.tableRow,
                          isLastItem && !item.gloss
                            ? { borderBottom: "1pt solid #666" }
                            : {},
                        ]}
                      >
                        <View style={[styles.cell, styles.codeCell]}></View>
                        <View style={[styles.cell, styles.accountCell]}>
                          <Text
                            style={{
                              fontSize: 8,
                              color: "#666",
                              fontStyle: "italic",
                            }}
                          >
                            {item.gloss}
                          </Text>
                        </View>
                        <Text style={[styles.cell, styles.amountCell]}></Text>
                        <Text style={[styles.cell, styles.amountCell]}></Text>
                        <Text style={[styles.cell, styles.amountCell]}></Text>
                        <Text
                          style={[
                            styles.cell,
                            styles.amountCell,
                            { borderRight: "none" },
                          ]}
                        ></Text>
                      </View>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Footer con totales */}
              <View style={styles.footer}>
                <Text style={styles.descriptionLabel}></Text>
                <Text style={styles.totalLabel}>TOTALES:</Text>
                <Text style={styles.totalAmount}>
                  {numberWithDecimals(totalDebitBs)}
                </Text>
                <Text style={styles.totalAmount}>
                  {numberWithDecimals(totalAssetBs)}
                </Text>
                <Text style={styles.totalAmount}>
                  {numberWithDecimals(totalDebitSus)}
                </Text>
                <Text style={[styles.totalAmount, { borderRight: "none" }]}>
                  {numberWithDecimals(totalAssetSus)}
                </Text>
              </View>
            </View>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
              }}
            >
              <Image
                style={{ transform: "scale(1.01)" }}
                src="https://res.cloudinary.com/joeldes/image/upload/v1763574235/Gamarra/ad31bf72-d62c-44c5-bdbc-33fcbc22faa5.png"
              ></Image>
            </View>

            {/* Sección de firmas */}
            {/* <View style={styles.signatureSection}>
              <View style={styles.signatureBox}>
                <Text style={styles.signatureLabel}>PREPARADO</Text>
              </View>
              <View style={styles.signatureBox}>
                <Text style={styles.signatureLabel}>REVISADO</Text>
              </View>
              <View style={styles.signatureBox}>
                <Text style={styles.signatureLabel}>APROBADO</Text>
              </View>
              <View style={styles.signatureBoxLast}>
                <View style={styles.signatureField}>
                  <View style={styles.signatureFieldLabel}>
                    <Text style={{ paddingLeft: 5 }}>NOMBRE:</Text>
                  </View>
                </View>
                <View style={styles.signatureField}>
                  <View style={styles.signatureFieldLabel}>
                    <Text style={{ paddingVertical: 5 }}></Text>
                  </View>
                </View>
                <View style={styles.signatureField}>
                  <View style={styles.signatureFieldLabel}>
                    <Text style={{ paddingLeft: 5 }}>CI:</Text>
                  </View>
                </View>
                <View
                  style={[
                    styles.signatureField,
                    {
                      borderBottom: "none",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                      margin: 0,
                      height: "30px",
                    },
                  ]}
                >
                  <View style={styles.signatureFieldLabel}>
                    <Text style={{ paddingLeft: 5 }}>FIRMA:</Text>
                  </View>
                </View>
                <Text style={styles.signatureLabel}>RECIBI CONFORME</Text>
              </View>
            </View> */}
          </Page>
        </Document>
      </PDFViewer>
    </div>
  );
};
